/**
 * @React
 */
import { useEffect, useMemo } from "react";
/**
 * @RHF
 */
import { UseFormSetValue } from "react-hook-form";
/**
 * @Utilities
 */
import { isMoreThanOneYear, generateNewDeadline, isExpire } from "utilities/dates";
import { UploadedAttachments, UploadedAttachmentsCGS } from "types";

interface WatchedValues {
  actual_municipality: string;
  mountainous_settlement: string;
  fixed_assets_capital_expenditures?: number;
  funds_turnover?: number;
  machine_equipment_equipment?: number;
  vehicles_special_technic?: number;
  purchase_building_land?: number;
  building_repairs?: number;
  targeted_fixed_assets_capital_expenditures?: number;
  targeted_funds_turnover?: number;
  targeted_machine_equipment_equipment?: number;
  targeted_vehicles_special_technic?: number;
  targeted_purchase_building_land?: number;
  targeted_building_repairs?: number;
  nontargeted_fixed_assets_capital_expenditures?: number;
  nontargeted_funds_turnover?: number;
  nontargeted_machine_equipment_equipment?: number;
  nontargeted_vehicles_special_technic?: number;
  nontargeted_purchase_building_land?: number;
  nontargeted_building_repairs?: number;
  used_amount?: number;
  targeted_used_amount?: number;
  nontargeted_used_amount?: number;
  unfulfilled_used_amount_reason?: string;
  unfulfilled_used_amount_month_day?: number;
  unfulfilled_used_amount_recommendation_reason?: string;
  is_fullfilled_used_amount?: boolean;
  contract_signed_date?: Date | null;
  is_deadline_approve?: boolean;
  monitoring_new_deadline?: Date | null;
  is_loan_documentation_provided?: boolean;
  monitoring_deadline?: Date | null;
  repayment_amount?: number;
  is_repayment_amount?: boolean;
  attached_documents?: UploadedAttachments & UploadedAttachmentsCGS
}

export const useFormCalculations = (watchedValues: WatchedValues, setValue: UseFormSetValue<WatchedValues>, data: any) => {
  // Compute usedAmount & fixedAssets
  const approvedAmount = data.contract?.approved_amount || data.approved_amount;
  const usedAmount = useMemo(() => {
    const sum = [watchedValues.fixed_assets_capital_expenditures || 0, watchedValues.funds_turnover || 0, watchedValues.repayment_amount || 0].reduce((a, b) => a + b, 0);
    return Math.min(sum, approvedAmount);
  }, [watchedValues.fixed_assets_capital_expenditures, watchedValues.funds_turnover, watchedValues.repayment_amount, approvedAmount]);

  const fixedAssets = useMemo(
    () =>
      [
        watchedValues.machine_equipment_equipment || 0,
        watchedValues.vehicles_special_technic || 0,
        watchedValues.purchase_building_land || 0,
        watchedValues.building_repairs || 0,
      ].reduce((a, b) => a + b, 0),
    [watchedValues.machine_equipment_equipment, watchedValues.vehicles_special_technic, watchedValues.purchase_building_land, watchedValues.building_repairs]
  );

  const usedAmountTargeted = useMemo(() => {
    const sum = [watchedValues.targeted_fixed_assets_capital_expenditures || 0, watchedValues.targeted_funds_turnover || 0, watchedValues.repayment_amount || 0].reduce((a, b) => a + b, 0);
    return Math.min(sum, approvedAmount);
  }, [watchedValues.targeted_fixed_assets_capital_expenditures, watchedValues.targeted_funds_turnover, watchedValues.repayment_amount, approvedAmount]);

  const fixedAssetsTargeted = useMemo(
    () =>
      [
        watchedValues.targeted_machine_equipment_equipment || 0,
        watchedValues.targeted_vehicles_special_technic || 0,
        watchedValues.targeted_purchase_building_land || 0,
        watchedValues.targeted_building_repairs || 0
       ].reduce((a, b) => a + b, 0),
    [
      watchedValues.targeted_machine_equipment_equipment,
      watchedValues.targeted_vehicles_special_technic,
      watchedValues.targeted_purchase_building_land,
      watchedValues.targeted_building_repairs
    ]
  );

  const usedAmountNonTargeted = useMemo(() => {
    const sum = [watchedValues.nontargeted_fixed_assets_capital_expenditures || 0, watchedValues.nontargeted_funds_turnover || 0].reduce((a, b) => a + b, 0);
    return Math.min(sum, approvedAmount);
  }, [watchedValues.nontargeted_fixed_assets_capital_expenditures, watchedValues.nontargeted_funds_turnover, approvedAmount]);

  const fixedAssetsNonTargeted = useMemo(
    () =>
      [
        watchedValues.nontargeted_machine_equipment_equipment || 0,
        watchedValues.nontargeted_vehicles_special_technic || 0,
        watchedValues.nontargeted_purchase_building_land || 0,
        watchedValues.nontargeted_building_repairs || 0,
      ].reduce((a, b) => a + b, 0),
    [
      watchedValues.nontargeted_machine_equipment_equipment,
      watchedValues.nontargeted_vehicles_special_technic,
      watchedValues.nontargeted_purchase_building_land,
      watchedValues.nontargeted_building_repairs
    ]
  );

  const isDeadlineAprove = useMemo(() => {
    const isMonitoringDeadlineExpired = isExpire(new Date(), watchedValues.monitoring_deadline as Date);
    const isMoreThanOneYearResult = isMoreThanOneYear(
      watchedValues?.contract_signed_date ? new Date(watchedValues.contract_signed_date).getTime() : 0,
      watchedValues.unfulfilled_used_amount_month_day || 0
    );
    return isMoreThanOneYearResult && isMonitoringDeadlineExpired;
  }, [watchedValues.contract_signed_date, watchedValues.unfulfilled_used_amount_month_day, watchedValues.monitoring_deadline]);

  const isApprovedAmountMoreThanUsedAmount = useMemo(() => {
    const isUsedAmountZero = (watchedValues.used_amount || 0) === 0;
    const isUsedAmountLessThanApproved = (watchedValues.used_amount || 0) < (approvedAmount || 0);
    const isUsedAmountGreaterThanTargeted = (watchedValues.used_amount || 0) > (watchedValues.targeted_used_amount || 0);
    const is_documentation = !watchedValues.is_loan_documentation_provided;
    return isUsedAmountZero || isUsedAmountLessThanApproved || isUsedAmountGreaterThanTargeted || is_documentation;
  }, [watchedValues.used_amount, approvedAmount, watchedValues.targeted_used_amount, watchedValues.is_loan_documentation_provided]);

  const newDeadline = useMemo(
    () => generateNewDeadline(watchedValues.unfulfilled_used_amount_month_day || 0, data.monitoring_new_deadline ?? null),
    [watchedValues.unfulfilled_used_amount_month_day, data.monitoring_new_deadline]
  );

  useEffect(() => {
    setValue("unfulfilled_used_amount_month_day", 0);
  }, []);

  useEffect(() => {

    if (!watchedValues.is_repayment_amount) {
      setValue("repayment_amount", 0);
      setValue("attached_documents.repayment_amount", []);
    }

    if (isDeadlineAprove) {
      setValue("is_deadline_approve", false);
    } else {
      setValue("is_deadline_approve", true);
    }

    if ((watchedValues.used_amount || 0) < (watchedValues.targeted_used_amount || 0)) {
      setValue("targeted_used_amount", watchedValues.used_amount || 0);
      setValue("is_fullfilled_used_amount", true);
    } else {
      setValue("nontargeted_used_amount", (watchedValues.used_amount || 0) - (watchedValues.targeted_used_amount || 0));
      setValue("is_fullfilled_used_amount", false);
    }
    if ((watchedValues.funds_turnover || 0) < (watchedValues.targeted_funds_turnover || 0)) {
      setValue("targeted_funds_turnover", watchedValues.funds_turnover || 0);
    } else {
      setValue("nontargeted_funds_turnover", (watchedValues.funds_turnover || 0) - (watchedValues.targeted_funds_turnover || 0));
    }
    if ((watchedValues.machine_equipment_equipment || 0) < (watchedValues.targeted_machine_equipment_equipment || 0)) {
      setValue("targeted_machine_equipment_equipment", watchedValues.machine_equipment_equipment || 0);
    } else {
      setValue(
        "nontargeted_machine_equipment_equipment",
        (watchedValues.machine_equipment_equipment || 0) - (watchedValues.targeted_machine_equipment_equipment || 0)
      );
    }
    if ((watchedValues.purchase_building_land || 0) < (watchedValues.targeted_purchase_building_land || 0)) {
      setValue("targeted_purchase_building_land", watchedValues.purchase_building_land || 0);
    } else {
      setValue("nontargeted_purchase_building_land", (watchedValues.purchase_building_land || 0) - (watchedValues.targeted_purchase_building_land || 0));
    }
    if ((watchedValues.vehicles_special_technic || 0) < (watchedValues.targeted_vehicles_special_technic || 0)) {
      setValue("targeted_vehicles_special_technic", watchedValues.vehicles_special_technic || 0);
    } else {
      setValue("nontargeted_vehicles_special_technic", (watchedValues.vehicles_special_technic || 0) - (watchedValues.targeted_vehicles_special_technic || 0));
    }
    if ((watchedValues.building_repairs || 0) < (watchedValues.targeted_building_repairs || 0)) {
      setValue("targeted_building_repairs", watchedValues.building_repairs || 0);
    } else {
      setValue("nontargeted_building_repairs", (watchedValues.building_repairs || 0) - (watchedValues.targeted_building_repairs || 0));
    }
    if (isApprovedAmountMoreThanUsedAmount) {
      setValue("is_fullfilled_used_amount", false);
    } else {
      setValue("unfulfilled_used_amount_reason", "");
      setValue("unfulfilled_used_amount_month_day", 0);
      setValue("unfulfilled_used_amount_recommendation_reason", "");
      setValue("is_fullfilled_used_amount", true);
      setValue("is_deadline_approve", true);
    }
    if (usedAmount === 0) {
      setValue("is_fullfilled_used_amount", false);
    }
    setValue("fixed_assets_capital_expenditures", fixedAssets);
    setValue("used_amount", usedAmount);
    setValue("monitoring_new_deadline", newDeadline);
    setValue("targeted_fixed_assets_capital_expenditures", fixedAssetsTargeted);
    setValue("targeted_used_amount", usedAmountTargeted);
    setValue("nontargeted_fixed_assets_capital_expenditures", fixedAssetsNonTargeted);
    setValue("nontargeted_used_amount", usedAmountNonTargeted);
  }, [
    usedAmount,
    fixedAssets,
    usedAmountTargeted,
    fixedAssetsTargeted,
    usedAmountNonTargeted,
    fixedAssetsNonTargeted,
    setValue,
    watchedValues.building_repairs,
    watchedValues.machine_equipment_equipment,
    watchedValues.purchase_building_land,
    watchedValues.targeted_building_repairs,
    watchedValues.targeted_machine_equipment_equipment,
    watchedValues.targeted_purchase_building_land,
    watchedValues.targeted_vehicles_special_technic,
    watchedValues.vehicles_special_technic,
    watchedValues.funds_turnover,
    watchedValues.targeted_funds_turnover,
    watchedValues.used_amount,
    watchedValues.targeted_used_amount,
    watchedValues.nontargeted_used_amount,
    isDeadlineAprove,
    newDeadline,
    isApprovedAmountMoreThanUsedAmount,
    watchedValues.unfulfilled_used_amount_month_day,
    watchedValues.is_repayment_amount,
  ]);

  useEffect(() => {
    if (watchedValues.actual_municipality !== data["actual_municipality"]) {
      setValue("mountainous_settlement", "");
    }
  }, [watchedValues.actual_municipality, setValue, data]);

  // Visibility conditions for conditional fields
  const visibilityConditions = useMemo(
    () => ({
      repayment_amount: watchedValues.is_repayment_amount,
      unfulfilled_used_amount_reason: isApprovedAmountMoreThanUsedAmount,
      unfulfilled_used_amount_month_day: isApprovedAmountMoreThanUsedAmount,
      unfulfilled_used_amount_recommendation_reason:
        isApprovedAmountMoreThanUsedAmount &&
        isMoreThanOneYear(
          watchedValues?.contract_signed_date ? new Date(watchedValues.contract_signed_date).getTime() : 0,
          watchedValues.unfulfilled_used_amount_month_day || 0
        ),
      is_fullfilled_used_amount: !isApprovedAmountMoreThanUsedAmount,
      monitoring_new_deadline: isApprovedAmountMoreThanUsedAmount,
    }),
    [
      isApprovedAmountMoreThanUsedAmount,
      watchedValues.contract_signed_date,
      watchedValues.unfulfilled_used_amount_month_day,
      watchedValues.is_repayment_amount
    ]
  );

  return { usedAmount, fixedAssets, visibilityConditions };
};
